import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { isValidArray } from "../../Layout/Common/common";

import "react-loading-skeleton/dist/skeleton.css";
import { AutoCompleteResult } from "./autoCompleteResult";
import { debounce } from "lodash";
import { indexName, newproductindexName, searchIndex } from "../../Layout/Common/algoliaInstantLib";

const suggestionIndexName = `${process.env.REACT_APP_ALGOLIA_SUGGESTION_INDEX}`;

export const AutoCompleteSuggestionPanel = ({ search }) => {
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [hits, setHits] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const requestIdRef = useRef(0);

  const debouncedSetQuery = useMemo(() => debounce((val) => setQuery(val), 300), []);

  // Debounced, not immediate. `search` is the header input's value, so it changes on every
  // keystroke, and each committed query fires two Algolia requests (products + suggestions) that
  // cannot be cancelled once sent. Typing a 9-letter word un-debounced put ~18 requests on the
  // wire; pressing Enter then queued the search page's own request behind them, since browsers
  // allow only 6 connections per host.
  //
  // The URL effect below still calls setQuery directly (and cancels this) so landing on
  // /search?q=... stays instant - the debounce only applies to live typing.
  useEffect(() => {
    debouncedSetQuery(String(search ?? "").trim());
  }, [search, debouncedSetQuery]);

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchStr = params.get("q");
    debouncedSetQuery.cancel();
    setQuery(searchStr ?? "");
  }, [window.location.search]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;
    if (query) {
      fetchResults(isStale);
    } else {
      fetchNewResults(isStale);
    }
  }, [query]);
  const fetchResults = async (isStale) => {
    const result = await Promise.all([
      searchIndex(indexName, { query, hitsPerPage: 20, facets: ["category"], distinct: false }),
      searchIndex(suggestionIndexName, { query, hitsPerPage: 20 }),
    ]);
    if (isStale()) return;
    const [mainRes, suggestionRes] = result;
    let cateList = mainRes.facets?.category ?? null;
    setHits(mainRes.hits);
    let arrayCateFilter = [];
    if (cateList) {
      const sortedCategories = Object.entries(cateList)
        .sort((a, b) => b[1] - a[1])
        .flatMap(([name, count]) => name);
      arrayCateFilter = [...new Set(sortedCategories)];
    }

    if (isValidArray(suggestionRes.hits)) {
      setSuggestions(
        suggestionRes.hits.map((hit) => ({
          value: hit._highlightResult.query.value,
          query: hit.query,
          categories: arrayCateFilter.slice(0, 6),
        })),
      );
    } else {
      setSuggestions([
        {
          value: "<b>" + suggestionRes.query + "</b>",
          query: suggestionRes.query,
          categories: arrayCateFilter.slice(0, 6),
        },
      ]);
    }
  };

  const fetchNewResults = async (isStale) => {
    const result = await Promise.all([
      searchIndex(newproductindexName, {
        query,
        hitsPerPage: 100,
        facets: ["category"],
        facetFilters: ["categories.lvl0:New Products"],
        distinct: true,
      }),
      searchIndex(suggestionIndexName, { query, hitsPerPage: 20 }),
    ]);
    if (isStale()) return;
    const [mainRes, suggestionRes] = result;
    let cateList = mainRes.facets?.category ?? null;
    setHits(mainRes.hits);
    let arrayCateFilter = [];
    if (cateList) {
      const sortedCategories = Object.entries(cateList)
        .sort((a, b) => b[1] - a[1])
        .flatMap(([name, count]) => name);
      arrayCateFilter = [...new Set(sortedCategories)];
    }

    if (isValidArray(suggestionRes.hits)) {
      setSuggestions(
        suggestionRes.hits.map((hit) => ({
          value: hit._highlightResult.query.value,
          query: hit.query,
          categories: arrayCateFilter.slice(0, 6),
        })),
      );
    } else {
      setSuggestions([
        {
          value: "<b>" + suggestionRes.query + "</b>",
          query: suggestionRes.query,
          categories: arrayCateFilter.slice(0, 6),
        },
      ]);
    }
  };

  return (
    <Fragment>
      <AutoCompleteResult hits={hits} query={query} suggestions={suggestions} />
    </Fragment>
  );
};
